import { useCallback, useEffect, useState } from 'react';

import { MIXPANEL_EVENTS } from 'pages/leads/constants';
import useLeadsMixPanel from 'pages/leads/hooks/use-leads-mixpanel';
import { bool, func, node } from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@olist/design-system';
import { Box, Loading, Text } from '@olist/united';

import { ARTICLE_PRODUCTS_NOT_ALLOWED } from '../../../../../utils/external-urls';
import InvalidSegmentsAlert from './components/invalid-segment-alert';
import SegmentDropdown from './components/segment-dropdown';
import useSegments from './hooks/useSegments';

const Span = ({ children }) => <span>{children}</span>;
Span.propTypes = {
  children: node.isRequired,
};

function SegmentsForm({ isSubmittingForm, isUnqualifiedLead, onAnswer, setIsUnqualifiedLead }) {
  const { t } = useTranslation();

  const [segments, setSegments] = useState([]);
  const { unqualifiedSegmentsLabels } = useSegments(segments);
  const [showInvalidSegmentsAlert, setShowInvalidSegmentsAlert] = useState(isUnqualifiedLead);
  const isSegmentsEmpty = !segments.length;
  const isContinueBtnDisabled = isSegmentsEmpty;
  const mixpanelInstance = useLeadsMixPanel();

  const handleOnHideInvalidSegmentsAlert = useCallback(() => {
    setIsUnqualifiedLead(false);
    setShowInvalidSegmentsAlert(false);
  }, [setIsUnqualifiedLead, setShowInvalidSegmentsAlert]);

  useEffect(() => {
    handleOnHideInvalidSegmentsAlert();
  }, [segments, handleOnHideInvalidSegmentsAlert]);

  useEffect(() => {
    setShowInvalidSegmentsAlert(isUnqualifiedLead);
  }, [isUnqualifiedLead]);

  const handleOnAnswer = () => {
    if (isContinueBtnDisabled) return;
    onAnswer(segments);
  };

  const handleOnChangeSegments = (segmentsCategory) => {
    const selectedSegmentsKeys = segmentsCategory.map((option) => option.id);
    setSegments(selectedSegmentsKeys);
  };

  const handleHelpLinkClick = () => {
    mixpanelInstance?.track(MIXPANEL_EVENTS.CLICKED_CATEGORIES_PAGE_HELP_LINK);
  };

  return (
    <>
      <Box>
        <SegmentDropdown onChangeSegments={handleOnChangeSegments} />
      </Box>
      <Box mt={4}>
        <Trans i18nKey="prohibitedProducts">
          not all products can be sold by <b>olist store</b>.
          <Text.Link
            href={ARTICLE_PRODUCTS_NOT_ALLOWED}
            target="_blank"
            onClick={handleHelpLinkClick}
          >
            check out the list of prohibited products and unmet categories.
          </Text.Link>
        </Trans>
      </Box>
      <Box mt={4}>
        <Button
          data-ref="continue-button"
          type="submit"
          disabled={isContinueBtnDisabled}
          variation="primary"
          onClick={handleOnAnswer}
        >
          {isSubmittingForm ? <Loading variation="shadow" /> : t`next`}
        </Button>
      </Box>
      {showInvalidSegmentsAlert && (
        <InvalidSegmentsAlert invalidCategory={unqualifiedSegmentsLabels[0]} />
      )}
    </>
  );
}

SegmentsForm.propTypes = {
  isSubmittingForm: bool.isRequired,
  isUnqualifiedLead: bool.isRequired,
  onAnswer: func.isRequired,
  setIsUnqualifiedLead: func.isRequired,
};

export default SegmentsForm;
